// Case-related constants and configuration

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const EVENT_TYPES = [
  { value: 'phone_call', label: 'Phone Call', color: 'bg-[#FAA519] text-black' },
  { value: 'meeting', label: 'Meeting', color: 'bg-[#80298F] text-white' },
  { value: 'event_hearing', label: 'Event/Hearing', color: 'bg-[#DC3239] text-white' },
  { value: 'medical', label: 'Medical', color: 'bg-blue-500 text-white' },
  { value: 'deadline', label: 'Deadline', color: 'bg-red-600 text-white' },
  { value: 'other', label: 'Other', color: 'bg-gray-500 text-white' }
];

export const CASE_TYPES = [
  { value: 'personal_injury', label: 'Personal Injury' },
  { value: 'employment', label: 'Employment' }
];

// Employment case type identifiers
export const EMPLOYMENT_CASE_TYPES = ['wrongful_termination', 'employment', 'workplace_harassment', 'discrimination'];

// Personal injury case type identifiers (all others default to this)
export const PERSONAL_INJURY_CASE_TYPES = ['personal_injury', 'car_accident', 'medical_malpractice', 'wrongful_death', 'slip_and_fall', 'workplace_injury', 'product_liability'];

export const EMPLOYMENT_TASK_TEMPLATES = [
  {
    id: 'emp_1',
    title: 'Upload copy of your government I.D.',
    description: 'Upload a clear copy or photo of your driver\'s license, state ID, or passport.',
    whyNeeded: 'We need to verify your identity to protect your case and ensure compliance with legal requirements.'
  },
  {
    id: 'emp_2',
    title: 'Complete Client Questionnaire',
    description: 'Fill out and submit the required questionnaire about your employment and case details.',
    whyNeeded: 'Your answers help us understand your situation better, look for evidence, and build your case effectively from the start.'
  },
  {
    id: 'emp_3',
    title: 'Share Your Primary Care Physician(s) (PCP) for the last 10 Years',
    description: 'Provide the name and contact information for your main doctor(s) over the past 10 years. If you don\'t have the contact information, any information such as street name and city, would help us in locating the correct provider.',
    whyNeeded: 'This information helps us gather medical records relevant to your disability or severe emotional distress claims. In cases involving disability discrimination, harassment, or retaliation, we need to prove that you had a qualifying disability, and the best evidence of that are medical records.'
  },
  {
    id: 'emp_4',
    title: 'Upload Health Insurance Card',
    description: 'Upload a copy or photo of the front and back of your health insurance card.',
    whyNeeded: 'Health insurance details help us request medical records related to your disability or treatment for severe emotional distress, and verify if your health insurance provider is asserting any liens against your case.'
  },
  {
    id: 'emp_5',
    title: 'Submit Evidence Supporting Your Claims',
    description: 'Share any text messages, emails, photos, videos, or other evidence related to your case.',
    whyNeeded: 'Evidence strengthens your case and helps us prove your claims to the employer or court. Usually, the more evidence you have, the better the outcome.'
  },
  {
    id: 'emp_6',
    title: 'Share Social Media Handles',
    description: 'Provide your usernames or handles for social media accounts.',
    whyNeeded: 'Social media may contain relevant information or evidence that could impact your case. Oftentimes the opposing party or private investigator on their behalf will go through your social media looking for posts to make you look bad. We need to make sure we are ahead of them.'
  },
  {
    id: 'emp_7',
    title: 'Upload Medical Records',
    description: 'Provide any medical records related to your disability or emotional distress treatment.',
    whyNeeded: 'Medical records support your disability discrimination claim and/or show the impact of your employer\'s actions.'
  },
  {
    id: 'emp_8',
    title: 'Submit Employment Documents',
    description: 'Upload any records from your employer, such as handbooks, pay stubs, agreements, or termination letters.',
    whyNeeded: 'These documents help us understand your employment history and support your legal claims.'
  },
  {
    id: 'emp_9',
    title: 'Upload Doctor\'s Orders for Medical Leave/Restrictions',
    description: 'Provide doctor\'s notes or orders for medical leave or work restrictions related to your case.',
    whyNeeded: 'These orders show your medical needs and support disability-related claims.'
  },
  {
    id: 'emp_10',
    title: 'Submit Proof of Job Search After Termination',
    description: 'Upload proof that you have been looking for work since your termination (e.g., job applications, emails, notifications).',
    whyNeeded: 'Proof of job search is often required to show you are trying to reduce your losses after losing your job. Proof that you have been searching for a job leads to a much better outcome for your case.'
  },
  {
    id: 'emp_11',
    title: 'Share All Relevant Medical Providers',
    description: 'Provide names and contact details for all medical providers involved in the treatment of your disability and/or treatment for emotional distress as a result of your employer\'s wrongful actions.',
    whyNeeded: 'This helps us collect complete medical records related to the disability or extent of your emotional distress damages. This is critical to prove your case.'
  }
];

export const PERSONAL_INJURY_TASK_TEMPLATES = [
  {
    id: 'pi_1',
    title: 'Upload copy of your government I.D.',
    description: 'Upload a clear copy or photo of your driver\'s license, state ID, or passport.',
    whyNeeded: 'We need to verify your identity to protect your case and comply with legal requirements.'
  },
  {
    id: 'pi_2',
    title: 'Complete Client Questionnaire',
    description: 'Fill out and submit the required questionnaire about your case details.',
    whyNeeded: 'Your answers help us understand your situation better, look for evidence, and build your case effectively from the start.'
  },
  {
    id: 'pi_3',
    title: 'List All Your Primary Care Physician(s) (PCP) for the Last 10 Years',
    description: 'Provide the name and contact information for your primary care doctor(s) over the past 10 years.',
    whyNeeded: 'This helps us gather medical records and understand your medical history before and after the incident.'
  },
  {
    id: 'pi_4',
    title: 'Complete CACI 3905A General Damages Chart',
    description: 'Fill out and submit the CACI Chart regarding how your injuries have affected your daily life.',
    whyNeeded: 'This chart helps us explain the impact of your injuries and support your claim for damages.'
  },
  {
    id: 'pi_5',
    title: 'Upload Health Insurance Card',
    description: 'Upload a copy or photo of the front and back of your health insurance card.',
    whyNeeded: 'Health insurance details help us request medical records related to your treatment and verify if your health insurance provider is asserting any liens for benefits they have paid out.'
  },
  {
    id: 'pi_6',
    title: 'Upload Incident Report (if applicable)',
    description: 'Upload a traffic collision report (for car accidents), incident report (for slip/trip and fall), or police report (for criminal acts like assault).',
    whyNeeded: 'These reports give official details about the incident and may provide crucial information and evidence for your case.'
  },
  {
    id: 'pi_7',
    title: 'Upload Photos/Videos of the Scene and Injuries',
    description: 'Provide photos or videos of where the incident happened and of your injuries.',
    whyNeeded: 'Visual evidence helps show what happened and the extent of your injuries.'
  },
  {
    id: 'pi_8',
    title: 'Upload Photos of Physical Activity Before Incident',
    description: 'Upload a few photos of yourself doing physical activities before the incident.',
    whyNeeded: 'These photos show your physical condition before the injury and help demonstrate the impact of the incident.'
  },
  {
    id: 'pi_9',
    title: 'Upload Family Photos',
    description: 'Upload a few photos with you and your family before the incident.',
    whyNeeded: 'Family photos humanize you and help show your life before and after the incident and can support claims for emotional distress.'
  },
  {
    id: 'pi_10',
    title: 'Submit Evidence Supporting Your Claims',
    description: 'Share any text messages, emails, photos, videos, or other evidence related to your case.',
    whyNeeded: 'Evidence strengthens your case and helps us prove your claims.'
  },
  {
    id: 'pi_11',
    title: 'Upload a Copy or Photo of Your Car Insurance Declaration Page',
    description: 'Upload your car insurance declaration sheet or page if your case involves a vehicle.',
    whyNeeded: 'This document shows your coverage and can be important for insurance claims.'
  },
  {
    id: 'pi_12',
    title: 'Share Social Media Handles',
    description: 'Provide your usernames or handles for social media accounts.',
    whyNeeded: 'Social media may contain relevant information or evidence that could impact your case. We need to make sure we know what evidence the opposing party has that could potentially damage your case.'
  },
  {
    id: 'pi_13',
    title: 'Upload Medical Records Related to the Incident',
    description: 'Provide any medical records you have that relate to your injuries from the incident.',
    whyNeeded: 'Medical records show the nature and extent of your injuries and support your claim.'
  }
];

// Helper function to get task templates based on case type
export const getTaskTemplatesForCaseType = (caseType) => {
  if (EMPLOYMENT_CASE_TYPES.includes(caseType)) {
    return EMPLOYMENT_TASK_TEMPLATES;
  }
  return PERSONAL_INJURY_TASK_TEMPLATES;
};

// Helper function to get event type info
export const getEventTypeInfo = (value) => {
  return EVENT_TYPES.find(t => t.value === value) || EVENT_TYPES[EVENT_TYPES.length - 1];
};
